// Auth context and hook for authentication state

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, logout as apiLogout } from '@/features/auth/api';
import { refreshSession } from '@/lib/http';
import { getAccessToken, setAccessToken, setStoredUser, clearTokens, getStoredUser } from '@/lib/auth';
import { persister, setCacheOwner, getCacheOwner, clearPersistedCache } from '@/lib/queryPersister';
import { queryClient } from '@/lib/queryClient';
import type { User } from '@/types/api';
import i18n from '@/i18n/config';

// The persisted React Query cache holds financial data. On a shared device it
// must never render one user's cached data for another user. If the cache's
// previously-recorded owner differs from the user we just established at
// bootstrap, bust it (persisted store + in-memory) before letting the app
// render — the user being restored is legitimately logging in, so we only
// clear the CACHE here, never force a logout.
async function bustCacheIfOwnerChanged(currentUserId: number): Promise<void> {
  const previousOwner = await getCacheOwner();
  if (previousOwner !== undefined && previousOwner !== currentUserId) {
    await clearPersistedCache(persister);
    queryClient.clear();
  }
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithSession: (accessToken: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state on mount.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const storedUser = getStoredUser();
      const accessToken = getAccessToken();

      if (storedUser && accessToken) {
        // Fast path: a session is already cached locally — restore immediately.
        // Bust a stale other-user cache BEFORE exposing data (isLoading stays
        // true across the await, so nothing renders with the previous user's
        // persisted queries).
        await bustCacheIfOwnerChanged(storedUser.id);
        if (cancelled) return;
        setCacheOwner(storedUser.id);
        setUser(storedUser);
        setIsLoading(false);
        return;
      }

      // No local session — the access token may simply have been cleared
      // (e.g. a fresh localStorage) while the httpOnly refresh cookie is
      // still valid. Attempt a cookie-based refresh to find out, through the
      // shared `refreshSession()` guard (lib/http.ts) so bootstrap cannot
      // race a concurrent `authorizedFetch` refresh — or another tab's — into
      // a double rotation of the same cookie.
      //
      // NOTE: we deliberately do NOT call `refreshAccessToken()` here. That
      // wrapper's definitive-failure path calls `clearTokens()` +
      // `window.location.href = '/login'` before throwing — correct when a
      // 401 is hit mid-session by `apiFetch`, but wrong for a cold,
      // logged-out bootstrap: firing a hard redirect to `/login` while we're
      // already rendering (possibly already on) the login page would reload
      // it, re-run this same effect, 401 again, and redirect again — an
      // infinite reload loop. `refreshSession()` never redirects; on failure
      // bootstrap just renders logged-out silently (routing already sends
      // unauthenticated users to /login via ProtectedRoute).
      try {
        await refreshSession();

        // refreshSession stores the session in localStorage (also when the
        // refresh was satisfied by another tab while we waited on its lock).
        const refreshedUser = getStoredUser();
        if (!refreshedUser) {
          throw new Error('Bootstrap refresh did not establish a session');
        }
        if (cancelled) return;
        await bustCacheIfOwnerChanged(refreshedUser.id);
        if (cancelled) return;
        setCacheOwner(refreshedUser.id);
        setUser(refreshedUser);
      } catch {
        if (cancelled) return;
        setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiLogin(email, password);
    setAccessToken(response.accessToken);
    setStoredUser(response.user);
    // /login has no already-authenticated guard, so User B may log in while
    // User A is still logged in in the same tab. The in-memory queryClient
    // still holds A's financial data (B would see it) and the owner key would
    // be silently overwritten to B without ever busting A's blob. Bust the
    // resident cache (in-memory + persisted) before the new session proceeds
    // whenever a *different* previous owner is recorded (Finding 1).
    const previousOwner = await getCacheOwner();
    if (previousOwner !== undefined && previousOwner !== response.user.id) {
      await clearPersistedCache(persister);
      queryClient.clear();
    }
    setUser(response.user);
    setCacheOwner(response.user.id);
    // Apply the user's stored language preference from the database at login
    if (response.user.language) {
      await i18n.changeLanguage(response.user.language);
    }
  };

  const loginWithSession = (accessToken: string, userData: User) => {
    // Clear the in-memory cache SYNCHRONOUSLY, before anything else. Callers
    // like InviteAcceptPage call loginWithSession() and then immediately
    // navigate() — if the previous user's queries were still resident when
    // the next page mounts, its useQuery hooks (keys aren't user-scoped)
    // could render that stale, previous user's financial data for however
    // long the async owner-mismatch bust below takes to resolve. This entry
    // point only ever establishes a brand-new session (invite-accept /
    // email-verify), so there is no same-user cache worth preserving at this
    // moment — clearing unconditionally is always safe.
    queryClient.clear();
    setAccessToken(accessToken);
    setStoredUser(userData);
    setUser(userData);
    // The PERSISTED store still needs the same cross-user-leak guard as
    // login(), but getCacheOwner() is an async IDB read, so run the
    // owner-mismatch bust fire-and-forget. The in-memory side is already
    // handled by the synchronous clear() above; this only busts the
    // persisted cache when a different owner is resident. setCacheOwner runs
    // after the (possible) bust so it never clears the new owner key.
    // (Bootstrap's bustCacheIfOwnerChanged remains as defense.)
    void getCacheOwner().then(async (previousOwner) => {
      if (previousOwner !== undefined && previousOwner !== userData.id) {
        await clearPersistedCache(persister);
      }
      setCacheOwner(userData.id);
    });
    if (userData.language) {
      i18n.changeLanguage(userData.language);
    }
  };

  const logout = () => {
    apiLogout().catch(() => {
      // Ignore errors on logout - always clear tokens
    });
    // Fire-and-forget, like apiLogout() above — don't block navigation on
    // IDB. The cache holds financial data, so nothing may survive for
    // whoever logs in next on this device.
    clearPersistedCache(persister).catch(() => {
      // Ignore errors clearing the persisted cache on logout
    });
    queryClient.clear();
    clearTokens();
    setUser(null);
    navigate('/login');
  };

  const isAuthenticated = !!getAccessToken();

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, loginWithSession, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
